// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CmsEditingMode from "@hardwoods/App/Components/CMS/CmsEditingMode";
import GridContainer, { GridContainerProps } from "@hardwoods/MobiusOverrides/GridContainer";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasShellContext;

interface MyAccountDetailsLayoutStyle {
    container?: InjectableCss;
    header?: InjectableCss;
    productListContainer?: InjectableCss;
    gridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    orderSummaryGridItem?: GridItemProps;
}

const styles: MyAccountDetailsLayoutStyle = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            > div {
                margin-bottom: 30px;
                &:first-child {
                    margin-bottom: 50px;
                }
                &:last-child {
                    margin-bottom: 0;
                }
            }

            @media print {
                display: block;
            }
        `,
    },
    header: {
        css: css`
            &,
            & > div {
                width: 100%;
            }
        `,
    },
    productListContainer: {
        css: css`
            &,
            & > div {
                width: 100%;
            }
        `,
    },
    gridContainer: {
        gap: 30,
    },
    infoGridItem: {
        width: [12, 12, 12, 9, 9],
        printWidth: 12,
        css: css`
            > div {
                width: 100%;
            }

            @media print {
                max-width: 100%;
                flex-basis: 100%;
            }

            ${({ isEditing }: any) =>
                !isEditing &&
                css`
                    ${xScreen.lte(1330)} {
                        max-width: 100%;
                        flex-basis: 100%;
                    }
                `}
        `,
    },
    orderSummaryGridItem: {
        width: [12, 12, 12, 3, 3],
        printWidth: 12,
        css: css`
            min-width: 300px;
            > div {
                width: 100%;
            }

            @media print {
                max-width: 100%;
                flex-basis: 100%;
            }

            ${({ isEditing }: any) =>
                !isEditing &&
                css`
                    ${xScreen.lte(1330)} {
                        max-width: 100%;
                        flex-basis: 100%;
                    }
                `}
        `,
    },
};

const MyAccountDetailsLayout: FC<Props> = ({ id, shellContext }) => {
    const props = { isEditing: shellContext.isEditing };
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.header}>
                <CmsEditingMode editingChildren="Zone for Header">
                    <Zone contentId={id} zoneName="Content_header" />
                </CmsEditingMode>
            </StyledWrapper>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.infoGridItem} {...props}>
                    <CmsEditingMode editingChildren="Zone for Info">
                        <Zone contentId={id} zoneName="Content_infoContainer" />
                    </CmsEditingMode>
                </GridItem>
                <GridItem {...styles.orderSummaryGridItem} {...props}>
                    <CmsEditingMode editingChildren="Zone for OrderSummary">
                        <Zone contentId={id} zoneName="Content_orderSummaryContainer" />
                    </CmsEditingMode>
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.productListContainer}>
                <CmsEditingMode editingChildren="Zone for ProductLists">
                    <Zone contentId={id} zoneName="Content_ProductLists" />
                </CmsEditingMode>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const categoryLayoutWidget: WidgetModule = {
    component: withIsInShell(MyAccountDetailsLayout),
    definition: {
        group: "Dashboard" as any,
        displayName: "MyAccount Details Layout",
    },
};

export default categoryLayoutWidget;
